"use client";

import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import usePatientAvailability from "../../../hooks/usePatientAvailability";
import "./Manageappointmentmodal.css";

/* ── helpers ── */
function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

const MONTHS_EN = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const MONTHS_FR = [
  "Janvier","Février","Mars","Avril","Mai","Juin",
  "Juillet","Août","Septembre","Octobre","Novembre","Décembre",
];
const MONTHS_AR = [
  "يناير","فبراير","مارس","أبريل","مايو","يونيو",
  "يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر",
];

const DAYS_EN = ["Su","Mo","Tu","We","Th","Fr","Sa"];
const DAYS_FR = ["Di","Lu","Ma","Me","Je","Ve","Sa"];
const DAYS_AR = ["أح","إث","ثل","أر","خم","جم","سب"];

const toDateKey = (dateObj) => {
  if (!(dateObj instanceof Date)) return "";
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
  const d = String(dateObj.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const toFullISO = (timeValue, dateKey) => {
  if (!timeValue) return null;
  const str = String(timeValue);
  if (str.includes("T")) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  if (dateKey) {
    const time = str.length === 5 ? `${str}:00` : str;
    const combined = new Date(`${dateKey}T${time}`);
    return Number.isNaN(combined.getTime()) ? null : combined.toISOString();
  }
  return null;
};

/* ── Month/day label helper based on i18n language ── */
function useCalendarLabels() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2);
  const months = lang === "ar" ? MONTHS_AR : lang === "fr" ? MONTHS_FR : MONTHS_EN;
  const days   = lang === "ar" ? DAYS_AR   : lang === "fr" ? DAYS_FR   : DAYS_EN;
  return { months, days };
}

/* ================================================================
   COMPONENT
================================================================ */
export default function ManageAppointmentModal({
  item,
  medecinId,
  onClose,
  onAccept,
  onCancel,
  onReschedule,
}) {
  const { t } = useTranslation("appointments");
  const { months, days } = useCalendarLabels();

  const [view, setView]                       = useState("main");
  const [isSubmitting, setIsSubmitting]       = useState(false);
  const [rescheduleError, setRescheduleError] = useState(null);

  const today = new Date();
  const [calYear, setCalYear]   = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [selDate, setSelDate]   = useState(null);
  const [selSlot, setSelSlot]   = useState(null);

  const { slotsByKey, loadingSlots, slotsError, loadSlots } = usePatientAvailability();

  const doctorId =
    medecinId        ||
    item?.medecinId  ||
    item?.doctorId   ||
    item?.doctor?.id ||
    item?.doctor?.pgId;

  const patientName = item?.name || "Patient";

  const apptDate =
    item?.startTime || item?.date
      ? new Date(item.startTime || item.date).toLocaleDateString("en-CA")
      : "—";
  const apptTime   = item?.time   || "—";
  const apptReason = item?.reason || item?.state || t("card.consultation");

  const selectedDateKey = selDate ? toDateKey(selDate) : "";
  const slotsKey        = doctorId && selectedDateKey ? `${doctorId}|${selectedDateKey}` : "";
  const availableSlots  = slotsByKey?.[slotsKey] || [];

  /* ── calendar cells ── */
  const daysInMonth   = getDaysInMonth(calYear, calMonth);
  const firstWeekDay  = getFirstDayOfMonth(calYear, calMonth);
  const prevMonthDays = getDaysInMonth(calYear, calMonth - 1);

  const cells = useMemo(() => {
    const result = [];
    for (let i = 0; i < firstWeekDay; i++)
      result.push({ day: prevMonthDays - firstWeekDay + 1 + i, ghost: true });
    for (let d = 1; d <= daysInMonth; d++)
      result.push({ day: d, ghost: false, dateObj: new Date(calYear, calMonth, d) });
    const remaining = 42 - result.length;
    for (let d = 1; d <= remaining; d++)
      result.push({ day: d, ghost: true });
    return result;
  }, [calYear, calMonth, daysInMonth, firstWeekDay, prevMonthDays]);

  const isToday = (dateObj) => {
    if (!dateObj) return false;
    const t = new Date();
    return (
      dateObj.getDate()     === t.getDate()     &&
      dateObj.getMonth()    === t.getMonth()     &&
      dateObj.getFullYear() === t.getFullYear()
    );
  };

  const isSelected = (dateObj) =>
    dateObj && selDate ? dateObj.toDateString() === selDate.toDateString() : false;

  const isPast = (dateObj) => {
    if (!dateObj) return false;
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return dateObj < t;
  };

  const prevMonth = () => {
    setSelDate(null);
    setSelSlot(null);
    if (calMonth === 0) { setCalMonth(11); setCalYear((y) => y - 1); }
    else setCalMonth((m) => m - 1);
  };

  const nextMonth = () => {
    setSelDate(null);
    setSelSlot(null);
    if (calMonth === 11) { setCalMonth(0); setCalYear((y) => y + 1); }
    else setCalMonth((m) => m + 1);
  };

  const handleSelectDate = async (dateObj) => {
    if (!dateObj || isPast(dateObj)) return;
    setSelDate(dateObj);
    setSelSlot(null);
    setRescheduleError(null);
    if (doctorId) await loadSlots(doctorId, toDateKey(dateObj));
  };

  const handleConfirmReschedule = async () => {
    if (!selDate || !selSlot || isSubmitting) return;

    setRescheduleError(null);
    setIsSubmitting(true);

    try {
      const startTime = toFullISO(selSlot.startTime, selectedDateKey);
      const endTime   = toFullISO(selSlot.endTime,   selectedDateKey);

      if (!startTime) {
        throw new Error(
          `startTime invalide — reçu: "${selSlot.startTime}", date: "${selectedDateKey}"`
        );
      }
      if (!endTime) {
        throw new Error(
          `endTime invalide — reçu: "${selSlot.endTime}", date: "${selectedDateKey}"`
        );
      }

      await onReschedule({
        appointment: item,
        date:        selectedDateKey,
        slot:        selSlot,
        startTime,
        endTime,
      });

      onClose();
    } catch (err) {
      console.error("❌ [ManageModal] reschedule failed:", err);
      setRescheduleError(err?.message || t("manageModal.slotsError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ================================================================
     RENDER
  ================================================================ */
  return (
    <>
      <div className="manage-appt-overlay" onClick={onClose} />
      <div className="manage-appt-modal">

        {/* header */}
        <div className="manage-appt-header">
          <div>
            <p className="manage-appt-header-title">{t("manageModal.title")}</p>
            <p className="manage-appt-header-sub">
              {patientName} • {apptDate} at {apptTime}
            </p>
          </div>
          <button type="button" className="manage-appt-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* ── MAIN VIEW ── */}
        {view === "main" && (
          <>
            <div className="manage-appt-patient-card">
              <div className="manage-appt-patient-card-left">
                <p className="manage-appt-patient-name">{patientName}</p>
                <p className="manage-appt-patient-phone">
                  {item?.phone || item?.email || "—"}
                </p>
                <div className="manage-appt-meta-row">
                  <span><strong>{t("table.dateTime")}:</strong> {apptDate}</span>
                  <span><strong>{t("card.reason")}:</strong> {apptReason}</span>
                </div>
              </div>
              <span className="manage-appt-source-badge">
                {item?.source || item?.type || "local"}
              </span>
            </div>

            <button
              type="button"
              className="manage-appt-action-btn manage-appt-accept-btn"
              onClick={() => { onAccept?.(); onClose(); }}
            >
              <svg viewBox="0 0 16 16" fill="none" width="15" height="15">
                <circle cx="8" cy="8" r="7" stroke="#fff" strokeWidth="1.4" />
                <path d="M5 8.5l2 2 4-4" stroke="#fff" strokeWidth="1.5"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {t("manageModal.accept")}
            </button>

            <button
              type="button"
              className="manage-appt-action-btn manage-appt-reschedule-btn"
              onClick={() => setView("reschedule")}
            >
              <svg viewBox="0 0 16 16" fill="none" width="15" height="15">
                <rect x="2" y="3" width="12" height="11" rx="2" stroke="#374151" strokeWidth="1.3" />
                <path d="M5 1v2M11 1v2M2 7h12" stroke="#374151" strokeWidth="1.3" strokeLinecap="round" />
              </svg>
              {t("manageModal.reschedule")}
            </button>

            <button
              type="button"
              className="manage-appt-action-btn manage-appt-cancel-btn"
              onClick={() => { onCancel?.(); onClose(); }}
            >
              <svg viewBox="0 0 16 16" fill="none" width="14" height="14">
                <path d="M4 4l8 8M12 4l-8 8" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              {t("manageModal.cancel")}
            </button>
          </>
        )}

        {/* ── RESCHEDULE VIEW ── */}
        {view === "reschedule" && (
          <>
            <p className="manage-appt-section-label">{t("manageModal.selectDate")}</p>

            {!doctorId && (
              <div className="manage-appt-warning">
                {t("manageModal.noDoctorIdWarning")}
              </div>
            )}

            {/* calendrier */}
            <div className="manage-appt-calendar">
              <div className="manage-appt-cal-nav">
                <button type="button" className="manage-appt-cal-nav-btn" onClick={prevMonth}>‹</button>
                <span className="manage-appt-cal-month-label">
                  {months[calMonth]} {calYear}
                </span>
                <button type="button" className="manage-appt-cal-nav-btn" onClick={nextMonth}>›</button>
              </div>
              <div className="manage-appt-cal-grid">
                {days.map((d) => (
                  <div key={d} className="manage-appt-cal-day-header">{d}</div>
                ))}
                {cells.map((c, i) => {
                  const past = !c.ghost && c.dateObj && isPast(c.dateObj);
                  const tod  = !c.ghost && c.dateObj && isToday(c.dateObj);
                  const sel  = !c.ghost && c.dateObj && isSelected(c.dateObj);
                  return (
                    <div
                      key={i}
                      onClick={() => !c.ghost && !past && handleSelectDate(c.dateObj)}
                      className={[
                        "manage-appt-cal-cell",
                        c.ghost ? "is-ghost"    : "",
                        past    ? "is-past"     : "",
                        tod     ? "is-today"    : "",
                        sel     ? "is-selected" : "",
                      ].filter(Boolean).join(" ")}
                    >
                      {c.day}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* slots */}
            <p className="manage-appt-section-label">{t("manageModal.selectTime")}</p>

            {loadingSlots && (
              <div className="manage-appt-slots-state">{t("manageModal.loadingSlots")}</div>
            )}
            {!!slotsError && !loadingSlots && (
              <div className="manage-appt-warning">{t("manageModal.slotsError")}</div>
            )}
            {!selDate && !loadingSlots && (
              <div className="manage-appt-slots-state">
                {t("manageModal.selectDateFirst")}
              </div>
            )}
            {selDate && !loadingSlots && !doctorId && (
              <div className="manage-appt-slots-state">
                {t("manageModal.noDoctorId")}
              </div>
            )}
            {selDate && doctorId && !loadingSlots && availableSlots.length === 0 && (
              <div className="manage-appt-slots-state">
                {t("manageModal.noSlots")}
              </div>
            )}
            {selDate && doctorId && availableSlots.length > 0 && (
              <div className="manage-appt-time-grid">
                {availableSlots.map((slot) => (
                  <button
                    type="button"
                    key={slot.id || slot.startTime}
                    onClick={() => { setSelSlot(slot); setRescheduleError(null); }}
                    className={`manage-appt-time-slot ${
                      selSlot?.startTime === slot.startTime ? "is-selected" : ""
                    }`}
                  >
                    {formatTime(
                      slot.startTime?.includes("T")
                        ? slot.startTime
                        : `${selectedDateKey}T${slot.startTime}`
                    )}
                  </button>
                ))}
              </div>
            )}

            {rescheduleError && (
              <div className="manage-appt-warning" style={{ marginTop: 8 }}>
                {rescheduleError}
              </div>
            )}

            {/* footer */}
            <div className="manage-appt-reschedule-footer">
              <button
                type="button"
                className="manage-appt-back-btn"
                onClick={() => {
                  setView("main");
                  setSelDate(null);
                  setSelSlot(null);
                  setRescheduleError(null);
                }}
              >
                {t("manageModal.back")}
              </button>
              <button
                type="button"
                className="manage-appt-confirm-reschedule-btn"
                disabled={!selDate || !selSlot || isSubmitting}
                onClick={handleConfirmReschedule}
              >
                {isSubmitting ? t("manageModal.rescheduling") : t("manageModal.confirmReschedule")}
              </button>
            </div>
          </>
        )}

      </div>
    </>
  );
}